import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Settings as Cog, Image as ImageIcon, Save, AlertCircle, Users as UsersIcon, Trash2, Ban, Search as SearchIcon, ShieldOff } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [maxWidth, setMaxWidth] = useState(1200);
  const [jpegQuality, setJpegQuality] = useState(78);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  // Users management
  const [userQuery, setUserQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [blockOnDelete, setBlockOnDelete] = useState({});

  // Blocklist
  const [blocklist, setBlocklist] = useState([]);
  const [newBlockEmail, setNewBlockEmail] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");

  const loadUsers = useCallback(async (q = "") => {
    try {
      const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
      setUsers(data.users);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load users");
    }
  }, []);

  const loadBlocklist = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/blocklist");
      setBlocklist(data.blocklist);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load blocklist");
    }
  }, []);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get("/admin/settings"),
          api.get("/admin/stats"),
        ]);
        setSettings(s.data);
        setMaxWidth(s.data.max_width);
        setJpegQuality(s.data.jpeg_quality);
        setStats(st.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load admin data");
      }
      loadUsers();
      loadBlocklist();
    })();
  }, [user, loadUsers, loadBlocklist]);

  if (loading || user === null) return <div className="p-12 text-[#8A8A8A]">Loading…</div>;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.patch("/admin/settings", {
        max_width: Number(maxWidth),
        jpeg_quality: Number(jpegQuality),
      });
      setSettings(data);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteUser = async (u) => {
    const block = !!blockOnDelete[u.id];
    if (!window.confirm(
      `Delete @${u.screen_name} (${u.email})? This removes their account, albums, and photos of them.` +
      (block ? "\n\nTheir email will also be BLOCKED from signing up again." : "")
    )) return;
    try {
      await api.delete(`/admin/users/${u.id}`, { params: { block } });
      toast.success(`Deleted @${u.screen_name}${block ? " and blocked their email" : ""}`);
      loadUsers(userQuery);
      if (block) loadBlocklist();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete user");
    }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    if (!newBlockEmail.trim()) return;
    try {
      await api.post("/admin/blocklist", { email: newBlockEmail.trim(), reason: newBlockReason.trim() });
      toast.success("Email blocked");
      setNewBlockEmail(""); setNewBlockReason("");
      loadBlocklist();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't block email");
    }
  };

  const removeBlock = async (email) => {
    try {
      await api.delete(`/admin/blocklist/${encodeURIComponent(email)}`);
      toast.success("Email unblocked");
      loadBlocklist();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't unblock email");
    }
  };

  // Estimate avg photo size at the current quality (very rough)
  const estimateKb = Math.round(((maxWidth / 1200) ** 2) * (jpegQuality / 78) * 220);

  return (
    <div className="paper-grain min-h-screen" data-testid="admin-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="font-script text-2xl text-[#8C3A3A]">control room</p>
        <h1 className="font-serif-display text-4xl mb-8 flex items-center gap-3">
          <Cog className="w-8 h-8"/> Admin
        </h1>

        {error && (
          <div className="paper-card p-4 mb-6 flex items-center gap-2 text-[#8C3A3A]" data-testid="admin-error">
            <AlertCircle className="w-4 h-4"/> {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="admin-stats">
            <Stat label="Users" value={stats.users}/>
            <Stat label="Photos" value={stats.photos}/>
            <Stat label="Pending" value={stats.pending_photos}/>
            <Stat label="Storage" value={`${stats.total_mb} MB`}/>
          </div>
        )}

        {/* Image settings */}
        <div className="paper-card p-8">
          <div className="flex items-center gap-2 mb-6">
            <ImageIcon className="w-5 h-5 text-[#3A5A40]"/>
            <h2 className="font-serif-display text-2xl">Image upload settings</h2>
          </div>
          <p className="text-sm text-[#5C5C5C] mb-6">
            These settings apply to all new photo uploads. Smaller width and lower quality save storage but reduce image quality.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">
                Max width (400 – 4000 px)
              </label>
              <input
                data-testid="admin-max-width"
                type="number" min={400} max={4000} step={100}
                value={maxWidth} onChange={(e) => setMaxWidth(e.target.value)}
                className="mp-input"
              />
              <p className="text-xs text-[#8A8A8A] mt-1">Photos wider than this are downsized. Default 1200.</p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">
                JPEG quality (40 – 95)
              </label>
              <input
                data-testid="admin-jpeg-quality"
                type="number" min={40} max={95}
                value={jpegQuality} onChange={(e) => setJpegQuality(e.target.value)}
                className="mp-input"
              />
              <p className="text-xs text-[#8A8A8A] mt-1">Lower = smaller files. 70-80 is a sweet spot. Default 78.</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-[#F0EBE1] text-sm">
            <strong>Estimate:</strong> ~{estimateKb} KB per photo at these settings.
            That's roughly <strong>{Math.round(1024 * 1024 / estimateKb).toLocaleString()}</strong> photos per GB of storage.
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary"
              data-testid="admin-save-btn"
            >
              <Save className="w-4 h-4"/> {saving ? "Saving…" : "Save settings"}
            </button>
            {savedAt && <span className="text-xs text-[#3A5A40]">Saved {savedAt}</span>}
          </div>
        </div>

        {/* User management */}
        <div className="paper-card p-8 mt-10" data-testid="admin-users-section">
          <div className="flex items-center gap-2 mb-6">
            <UsersIcon className="w-5 h-5 text-[#3A5A40]"/>
            <h2 className="font-serif-display text-2xl">Users</h2>
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); loadUsers(userQuery); }}
            className="flex gap-2 mb-6"
          >
            <input
              data-testid="admin-user-search"
              className="mp-input flex-1"
              placeholder="Search by screen name, email, or name…"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
            />
            <button type="submit" className="btn-secondary" data-testid="admin-user-search-btn">
              <SearchIcon className="w-4 h-4"/> Search
            </button>
          </form>

          <div className="space-y-2" data-testid="admin-users-list">
            {users.length === 0 && <p className="text-sm text-[#8A8A8A] italic">No users found.</p>}
            {users.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-4 p-3 border border-[#E5E0D8] bg-[#F9F6F0] flex-wrap"
                data-testid={`admin-user-${u.id}`}
              >
                <div className="min-w-0">
                  <p className="font-serif-display text-lg truncate">
                    @{u.screen_name}
                    {u.is_admin && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#8C3A3A]">admin</span>}
                  </p>
                  <p className="text-xs text-[#5C5C5C] truncate">{u.email}{u.name ? ` · ${u.name}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-[#5C5C5C] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!blockOnDelete[u.id]}
                      onChange={(e) => setBlockOnDelete({ ...blockOnDelete, [u.id]: e.target.checked })}
                      data-testid={`admin-block-toggle-${u.id}`}
                    />
                    Also block email
                  </label>
                  <button
                    onClick={() => deleteUser(u)}
                    disabled={u.is_admin}
                    className="btn-ghost text-sm text-[#8C3A3A] disabled:opacity-40"
                    title={u.is_admin ? "Can't delete an admin here" : "Delete user"}
                    data-testid={`admin-delete-user-${u.id}`}
                  >
                    <Trash2 className="w-4 h-4"/> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Email blocklist */}
        <div className="paper-card p-8 mt-10" data-testid="admin-blocklist-section">
          <div className="flex items-center gap-2 mb-6">
            <Ban className="w-5 h-5 text-[#8C3A3A]"/>
            <h2 className="font-serif-display text-2xl">Blocked emails</h2>
          </div>
          <p className="text-sm text-[#5C5C5C] mb-6">
            Emails on this list cannot register a new account. They'll see a clear message at signup.
          </p>
          <form onSubmit={addBlock} className="flex gap-2 mb-6 flex-wrap">
            <input
              data-testid="admin-block-email-input"
              type="email"
              className="mp-input flex-1 min-w-[200px]"
              placeholder="email@example.com"
              value={newBlockEmail}
              onChange={(e) => setNewBlockEmail(e.target.value)}
            />
            <input
              data-testid="admin-block-reason-input"
              className="mp-input flex-1 min-w-[160px]"
              placeholder="Reason (optional)"
              value={newBlockReason}
              onChange={(e) => setNewBlockReason(e.target.value)}
            />
            <button type="submit" className="btn-primary" data-testid="admin-block-add-btn">
              <Ban className="w-4 h-4"/> Block
            </button>
          </form>
          <div className="space-y-2" data-testid="admin-blocklist-list">
            {blocklist.length === 0 && <p className="text-sm text-[#8A8A8A] italic">No blocked emails.</p>}
            {blocklist.map((b) => (
              <div
                key={b.email}
                className="flex items-center justify-between gap-4 p-3 border border-[#E5E0D8] bg-[#F9F6F0]"
                data-testid={`admin-block-${b.email}`}
              >
                <div className="min-w-0">
                  <p className="text-[#1F1F1F] truncate">{b.email}</p>
                  {b.reason && <p className="text-xs text-[#8A8A8A] truncate">{b.reason}</p>}
                </div>
                <button
                  onClick={() => removeBlock(b.email)}
                  className="btn-ghost text-sm text-[#3A5A40]"
                  data-testid={`admin-unblock-${b.email}`}
                >
                  <ShieldOff className="w-4 h-4"/> Unblock
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="paper-card p-4">
      <p className="text-xs uppercase tracking-[0.18em] text-[#5C5C5C]">{label}</p>
      <p className="font-serif-display text-3xl mt-1">{value}</p>
    </div>
  );
}
